/**
 * /vendors/:vendorId/edit — edición y baja de un proveedor.
 *
 * "Eliminar" DESACTIVA, no borra. work_orders, expenses y
 * maintenance_schedules apuntan al proveedor con ON DELETE SET NULL: borrarlo
 * de verdad dejaría el historial de gastos sin decir quién hizo el trabajo, que
 * es justo lo que se consulta al revisar costes. Desactivado desaparece del
 * listado y de los selectores, que es lo que la gente quiere decir con
 * "bórralo", y se puede revertir.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { ApiError, deactivateVendor, getVendor, patchVendor } from '@/lib/api'
import { PageHeader } from '@/components/app-shell'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { VendorForm, type VendorFormValues } from '@/components/vendor-form'

export function VendorEditPage() {
  const { vendorId } = useParams({ from: '/vendors/$vendorId/edit' })
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [fields, setFields] = useState<Record<string, string>>({})
  const [confirmOff, setConfirmOff] = useState(false)

  const query = useQuery({
    queryKey: ['vendor', vendorId],
    queryFn: () => getVendor(vendorId),
  })

  const save = useMutation({
    mutationFn: (values: VendorFormValues) => patchVendor(vendorId, values),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['vendor', vendorId] })
      await qc.invalidateQueries({ queryKey: ['vendors'] })
      void navigate({ to: '/vendors' })
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message)
        setFields(err.fields ?? {})
      } else setError('No se pudo guardar.')
    },
  })

  const deactivate = useMutation({
    mutationFn: () => deactivateVendor(vendorId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['vendors'] })
      void navigate({ to: '/vendors' })
    },
    onError: (err) => {
      setConfirmOff(false)
      setError(err instanceof ApiError ? err.message : 'No se pudo desactivar.')
    },
  })

  const reactivate = useMutation({
    mutationFn: () => patchVendor(vendorId, { active: true }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['vendor', vendorId] })
      await qc.invalidateQueries({ queryKey: ['vendors'] })
    },
  })

  if (query.isLoading) return <p className="text-sm text-muted-foreground">Cargando…</p>
  if (query.isError || !query.data) {
    return <p className="text-sm text-danger">No se pudo cargar el proveedor.</p>
  }

  const vendor = query.data.vendor

  return (
    <>
      <PageHeader
        title={`Editar ${vendor.companyName}`}
        subtitle={vendor.active ? undefined : 'Desactivado — no aparece en el listado'}
        actions={
          <Link to="/vendors" className="text-sm text-muted-foreground hover:text-foreground">
            Cancelar
          </Link>
        }
      />

      <Card className="mx-auto max-w-xl">
        <CardBody>
          <VendorForm
            initial={vendor}
            submitLabel="Guardar cambios"
            pending={save.isPending}
            error={error}
            fieldErrors={fields}
            onSubmit={(values) => {
              setError(null)
              setFields({})
              save.mutate(values)
            }}
            extraActions={
              !vendor.active ? (
                <Button
                  type="button"
                  variant="outline"
                  className="sm:flex-1"
                  loading={reactivate.isPending}
                  onClick={() => reactivate.mutate()}
                >
                  Reactivar
                </Button>
              ) : confirmOff ? (
                <div className="flex gap-2 sm:flex-1">
                  <Button
                    type="button"
                    variant="danger"
                    className="flex-1"
                    loading={deactivate.isPending}
                    onClick={() => deactivate.mutate()}
                  >
                    Confirmar
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setConfirmOff(false)}>
                    No
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="sm:flex-1"
                  onClick={() => setConfirmOff(true)}
                >
                  Desactivar
                </Button>
              )
            }
          />
        </CardBody>
      </Card>
    </>
  )
}
